import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  listPortalAiThreads,
  createPortalAiThread,
  getPortalAiThread,
  postPortalAiThreadMessage,
} from '../../services/portalAiService';
import PortalAiAnswer from './PortalAiAnswer';
import PortalAiAnswerExpandable from './PortalAiAnswerExpandable';
import './portalAiWidget.css';

const PREFS_KEY = 'mboss.portal.ai.prefs';
const OPEN_MODES = ['floating', 'dock-right'];
const DEFAULT_MODE = 'floating';
const GROUNDING_MODES = ['auto', 'subject', 'knowledge'];

function loadPrefs() {
  try {
    return JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
  } catch (_) {
    return {};
  }
}

function savePrefs(prefs) {
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
}

function preferredOpenMode(prefs = {}) {
  if (OPEN_MODES.includes(prefs.lastOpenMode)) return prefs.lastOpenMode;
  if (OPEN_MODES.includes(prefs.mode)) return prefs.mode;
  return DEFAULT_MODE;
}

function normalizePrefs(raw = {}) {
  const lastOpenMode = OPEN_MODES.includes(raw.lastOpenMode)
    ? raw.lastOpenMode
    : OPEN_MODES.includes(raw.mode)
      ? raw.mode
      : DEFAULT_MODE;
  return {
    mode: OPEN_MODES.includes(raw.mode) || raw.mode === 'collapsed' ? raw.mode : DEFAULT_MODE,
    lastOpenMode,
    groundingMode: GROUNDING_MODES.includes(raw.groundingMode) ? raw.groundingMode : 'auto',
  };
}

export function portalAiPageClass(pathname = window.location.pathname) {
  return /(^|\/)invoices(?:\/|$)/.test(pathname)
    ? 'portal-ai-widget--invoices'
    : '';
}

/**
 * AI-UX3 — Portal AI Widget (Experience Layer).
 * Composes /api/client/ai threads; subject always self (server-enforced).
 * Session prefs (layout, grounding) mirror Admin AI / Phone widgets.
 */
function PortalAiWidget() {
  const [prefs, setPrefs] = useState(() => normalizePrefs(loadPrefs()));
  const [expanded, setExpanded] = useState(() => normalizePrefs(loadPrefs()).mode !== 'collapsed');
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [turns, setTurns] = useState([]);
  const [question, setQuestion] = useState('');
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);

  const openMode = preferredOpenMode(prefs);
  const groundingMode = prefs.groundingMode;

  const updatePrefs = useCallback((patch) => {
    setPrefs((prev) => {
      const next = normalizePrefs({ ...prev, ...patch });
      savePrefs(next);
      return next;
    });
  }, []);

  const expand = useCallback(
    (modeOverride) => {
      const nextMode = OPEN_MODES.includes(modeOverride) ? modeOverride : openMode;
      setExpanded(true);
      updatePrefs({ mode: nextMode, lastOpenMode: nextMode });
    },
    [openMode, updatePrefs]
  );

  const collapse = useCallback(() => {
    const lastOpenMode = preferredOpenMode(prefs);
    setExpanded(false);
    updatePrefs({ mode: 'collapsed', lastOpenMode });
  }, [prefs, updatePrefs]);

  const refreshThreads = useCallback(async () => {
    try {
      const res = await listPortalAiThreads({ status: 'open', limit: 20 });
      setThreads((res.data && res.data.data) || []);
    } catch (_) {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const threadsRes = await listPortalAiThreads({ status: 'open', limit: 20 });
        if (cancelled) return;
        setThreads((threadsRes.data && threadsRes.data.data) || []);
      } catch (ex) {
        if (!cancelled) {
          toast.error(
            (ex.response && ex.response.data && ex.response.data.error) ||
              'Failed to load assistant.'
          );
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [expanded]);

  useEffect(() => {
    if (bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [turns, busy]);

  const startThread = async () => {
    setBusy(true);
    try {
      const res = await createPortalAiThread({ pack: 'customers' });
      setActiveThread((res.data && res.data.data) || null);
      setTurns([]);
      await refreshThreads();
    } catch (ex) {
      toast.error(
        (ex.response && ex.response.data && ex.response.data.error) ||
          'Failed to start conversation.'
      );
    } finally {
      setBusy(false);
    }
  };

  const openThread = async (id) => {
    setBusy(true);
    try {
      const res = await getPortalAiThread(id);
      const data = (res.data && res.data.data) || {};
      setActiveThread(data);
      setTurns(data.turns || []);
    } catch (ex) {
      toast.error(
        (ex.response && ex.response.data && ex.response.data.error) ||
          'Failed to open conversation.'
      );
    } finally {
      setBusy(false);
    }
  };

  const sendChat = async (e) => {
    if (e) e.preventDefault();
    if (!activeThread) {
      toast.warn('Start or open a conversation first.');
      return;
    }
    const q = String(question || '').trim();
    if (!q) {
      toast.warn('Enter a question.');
      return;
    }
    setBusy(true);
    try {
      const res = await postPortalAiThreadMessage(activeThread.id, {
        question: q,
        mode: 'ask',
        grounding_mode: groundingMode,
      });
      const turn = res.data && res.data.data && res.data.data.turn;
      const thread =
        (res.data && res.data.data && res.data.data.thread) || activeThread;
      setActiveThread(thread);
      setQuestion('');
      if (turn) {
        setTurns((prev) => [
          ...prev,
          {
            id: turn.id,
            question: turn.question,
            answer: turn.answer,
          },
        ]);
      }
      await refreshThreads();
    } catch (ex) {
      toast.error(
        (ex.response && ex.response.data && ex.response.data.error) || 'Send failed.'
      );
    } finally {
      setBusy(false);
    }
  };

  const panelClass = [
    'portal-ai-widget',
    portalAiPageClass(),
    expanded ? `portal-ai-widget--${openMode}` : 'portal-ai-widget--hidden',
  ].filter(Boolean).join(' ');

  return (
    <>
      {!expanded && (
        <button
          type="button"
          className="portal-ai-fab"
          title="Open assistant"
          aria-label="Open portal assistant"
          onClick={() => expand()}
        >
          <i className="fa fa-magic" aria-hidden="true" />
        </button>
      )}
      {expanded && (
        <div className={panelClass} role="dialog" aria-label="Portal assistant">
          <header className="portal-ai-widget__header">
            <div>
              <h2 className="portal-ai-widget__title">Assistant</h2>
              <div className="portal-ai-widget__subtitle">
                Your account · Help articles · No staff metrics
              </div>
            </div>
            <div className="portal-ai-widget__header-actions">
              <button
                type="button"
                className="btn btn-sm btn-light"
                title="Floating"
                onClick={() =>
                  updatePrefs({ mode: 'floating', lastOpenMode: 'floating' })
                }
              >
                <i className="fa fa-window-maximize" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light"
                title="Dock right"
                onClick={() =>
                  updatePrefs({ mode: 'dock-right', lastOpenMode: 'dock-right' })
                }
              >
                <i className="fa fa-dedent" />
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light"
                title="Close"
                onClick={collapse}
              >
                <i className="fa fa-times" />
              </button>
            </div>
          </header>

          <div className="portal-ai-widget__toolbar">
            <select
              className="form-control form-control-sm"
              value={(activeThread && activeThread.id) || ''}
              onChange={(e) => {
                const id = e.target.value;
                if (id) openThread(id);
                else {
                  setActiveThread(null);
                  setTurns([]);
                }
              }}
              disabled={busy}
            >
              <option value="">— Select conversation —</option>
              {threads.map((t) => (
                <option key={t.id} value={t.id}>
                  #{t.id} {t.title || 'conversation'}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              disabled={busy}
              onClick={startThread}
            >
              New
            </button>
            <select
              className="form-control form-control-sm"
              value={groundingMode}
              onChange={(e) => updatePrefs({ groundingMode: e.target.value })}
              title="Grounding"
            >
              <option value="auto">auto</option>
              <option value="subject">my account</option>
              <option value="knowledge">help</option>
            </select>
          </div>

          <div className="portal-ai-widget__body" ref={bodyRef}>
            {!turns.length && (
              <p className="portal-ai-widget__empty text-muted small">
                Ask about your account, invoices, or how to use the portal.
              </p>
            )}
            {turns.map((t) => (
              <div key={t.id} className="portal-ai-widget__turn">
                <div className="portal-ai-widget__q">
                  <strong>You</strong>
                  <div>{t.question}</div>
                </div>
                <div className="portal-ai-widget__a">
                  <strong>Assistant</strong>
                  <PortalAiAnswerExpandable contentKey={t.id}>
                    <PortalAiAnswer content={t.answer} />
                  </PortalAiAnswerExpandable>
                </div>
              </div>
            ))}
            {busy && <div className="small text-muted px-2 py-1">Working…</div>}
          </div>

          <form className="portal-ai-widget__composer" onSubmit={sendChat}>
            <div className="portal-ai-widget__field">
              <textarea
                className="portal-ai-widget__input"
                rows={2}
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) return;
                  e.preventDefault();
                  if (busy) return;
                  sendChat(e);
                }}
                placeholder="Ask about your account or how to…"
                disabled={busy}
              />
              <button
                type="submit"
                className="portal-ai-widget__submit"
                disabled={busy}
                title="Send"
                aria-label="Send"
              >
                <i className="fa fa-paper-plane" aria-hidden />
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

export default PortalAiWidget;
